import {
  BookingQualificationRequirements,
  fetchGetLeadBookingQualificationRequirements,
  fetchPostLeadAction,
  fetchPostMeetingReconfirmation,
  LeadCreationResponse,
} from "@justhome/api-core";
import {
  BookingQualificationResponse,
  fetchProjectUpdateBookingQualification,
  fetchProjectUpdateBookingCalendarShown,
  fetchProjectUpdateBookingReconfirmationFormShown,
  fetchProjectUpdateReactivateProject,
  BookingQualificationStep,
} from "@justhome/api-projectupdate";
import { assign, fromPromise, setup } from "xstate";
import { MeetingScheduledEvent } from "../components/Meeting";
import { ReconfirmationFormValues } from "../components/ReconfirmationForm";
import {
  createGuard,
  hasPastMeetings,
  hasUnconfirmedMatchedProject,
  isDisqualified,
  isDisqualifiedFromOutput,
  isFromPartner,
  isSkipQualification,
  needsEmployment,
  needsEquity,
  needsPropertyViewingStatus,
  needsIncome,
  needsLeadWall,
  needsProperty,
  needsReconfirmation,
  needsResidence,
} from "./guards";
import { mapFormToBookingQualification } from "./mapper";
import { QualificationFormData } from "./types";
import { logger } from "@justhome/common";

export type ProjectDecision = "existing" | "new";

export type QualificationEvent =
  | { type: "NEXT"; data: QualificationFormData }
  | { type: "BACK" }
  | { type: "MEETING_SCHEDULED"; data: MeetingScheduledEvent }
  | { type: "RECONFIRMATION"; data: ReconfirmationFormValues }
  | { type: "PROJECT_DECISION"; data: ProjectDecision }
  | { type: "LEAD_CREATED"; data: LeadCreationResponse };

export interface QualificationContext {
  leadId?: string;
  lead?: LeadCreationResponse;
  formData: QualificationFormData;
  requirements?: BookingQualificationRequirements;
  firstStepId?: string;
  isError?: boolean;
  isDisqualified?: boolean;
  needsReconfirmation?: boolean;
  meetingTime?: string;
  propertyId?: string;
  contactId?: string;
  emailFromQuery?: string;
  partnerId?: string;
  projectId?: string;
  dealId?: string;
  matchedProject?: BookingQualificationRequirements["project"];
  projectDecisionMade?: boolean;
  phoneNumber?: string;
  skipQualification?: boolean;
}

export interface QualificationMachineInput {
  leadId?: string;
  lead?: LeadCreationResponse;
  propertyId?: string;
  contactId?: string;
  emailFromQuery?: string;
  partnerId?: string;
  projectId?: string;
  dealId?: string;
  isDisqualified?: boolean;
  needsReconfirmation?: boolean;
  skipQualification?: boolean;
}

const getRequirements = fromPromise<
  {
    requirements: BookingQualificationRequirements;
    qualification: BookingQualificationResponse;
  },
  { leadId?: string; projectId?: string; partnerId?: string; dealId?: string }
>(async ({ input }) => {
  const [qualification, requirements] = await Promise.all([
    fetchProjectUpdateBookingQualification({
      body: mapFormToBookingQualification({}),
      queryParams: {
        leadId: input?.leadId || "",
        projectId: input?.projectId || "",
        partnerId: input?.partnerId || "",
        dealId: input?.dealId || "",
      },
    }),
    fetchGetLeadBookingQualificationRequirements({
      queryParams: {
        leadId: input.leadId || "",
        projectId: input.projectId || "",
        partnerId: input?.partnerId || "",
        dealId: input?.dealId || "",
      },
    }),
  ]);
  return { requirements, qualification };
});

const saveStep = fromPromise<
  {
    isDisqualified: boolean;
    needsReconfirmation: boolean;
    projectId?: string;
    requirements?: BookingQualificationRequirements;
  },
  | {
      data: QualificationFormData;
      leadId?: string;
      projectId?: string;
      dealId?: string;
      qualificationStep?: BookingQualificationStep;
    }
  | undefined
>(async ({ input }) => {
  const response = await fetchProjectUpdateBookingQualification({
    body: mapFormToBookingQualification(input?.data ?? {}),
    queryParams: {
      leadId: input?.leadId || "",
      projectId: input?.projectId || "",
      dealId: input?.dealId || "",
      qualificationStep: input?.qualificationStep,
    },
  });
  return {
    isDisqualified: response.disqualified,
    needsReconfirmation: response.needsReconfirmation,
    projectId: response.projectId,
    requirements: response.requirements,
  };
});

const saveReconfirmation = fromPromise<
  void,
  {
    phoneNumber: string;
    leadId?: string;
    projectId?: string;
    partnerId?: string;
  }
>(async ({ input }) => {
  await fetchPostMeetingReconfirmation({
    body: {
      phoneNumber: input.phoneNumber,
      leadId: input.leadId!,
      projectId: input.projectId!,
      partnerId: input.partnerId,
    },
  });
});

const toMeetingStart = (meetingTime?: string) => {
  if (!meetingTime) {
    return undefined;
  }

  const meetingStart = new Date(meetingTime);
  if (Number.isNaN(meetingStart.getTime())) {
    return undefined;
  }

  return meetingStart.toISOString();
};

const saveLeadCallAction = fromPromise<
  void,
  {
    leadId: string;
    partnerId?: string;
    propertyId?: string;
    projectId?: string;
    meetingTime?: string;
  }
>(async ({ input }) => {
  await fetchPostLeadAction({
    pathParams: {
      leadId: input.leadId,
    },
    body: {
      action: "CALL",
      partnerId: input.partnerId,
      propertyId: input.propertyId,
      projectId: input.projectId,
      meetingStart: toMeetingStart(input.meetingTime),
    },
  });
});

const reactivateProject = fromPromise<
  void,
  {
    leadId: string;
    projectId: string;
  }
>(async ({ input }) => {
  await fetchProjectUpdateReactivateProject({
    pathParams: {
      leadId: input.leadId,
      projectId: input.projectId,
    },
  });
});

const makeSaveStepInput =
  (qualificationStep?: BookingQualificationStep) =>
  ({
    event,
    context,
  }: {
    event: QualificationEvent;
    context: QualificationContext;
  }) => {
    if (event.type === "NEXT") {
      return {
        data: event.data,
        leadId: context.leadId,
        projectId: context.projectId,
        dealId: context.dealId,
        qualificationStep,
      };
    }
    return undefined;
  };

export const qualificationMachine = setup({
  types: {
    context: {} as QualificationContext,
    events: {} as QualificationEvent,
  },
  actors: {
    saveStep,
    saveReconfirmation,
    getRequirements,
    saveLeadCallAction,
    reactivateProject,
  },
  guards: {
    needsLeadWall,
    needsEquity: createGuard(needsEquity),
    needsResidence: createGuard(needsResidence),
    needsEmployment: createGuard(needsEmployment),
    needsIncome: createGuard(needsIncome),
    needsPropertyViewingStatus: createGuard(needsPropertyViewingStatus),
    isDisqualified,
    isDisqualifiedFromOutput,
    isFromPartner,
    needsReconfirmation,
    needsProperty: createGuard(needsProperty),
    isSkipQualification,
    hasPastMeetings: createGuard(hasPastMeetings),
    hasUnconfirmedMatchedProject,
  },
  actions: {
    setMeetingTime: assign({
      meetingTime: ({ event }) => {
        if (event.type === "MEETING_SCHEDULED" && event.data) {
          return event.data.time;
        }
        return undefined;
      },
    }),
    saveFormData: assign({
      formData: ({ context, event }) => {
        if (event.type === "NEXT" && event.data) {
          return { ...context.formData, ...event.data };
        }
        return context.formData;
      },
    }),
    setFirstStepId: assign({
      firstStepId: (_, params) => {
        return params as string;
      },
    }),
    setLeadId: assign({
      leadId: ({ event }: any) => {
        return event.output?.leadId;
      },
      projectId: ({ event }: any) => {
        return event.output?.projectId;
      },
    }),
    setLead: assign({
      lead: ({ event }: any) => {
        if (event.type === "LEAD_CREATED" && event.data) {
          return event.data;
        }
        return undefined;
      },
      leadId: ({ event }: any) => {
        if (event.type === "LEAD_CREATED" && event.data) {
          return event.data.id;
        }
        return undefined;
      },
      projectId: ({ event }: any) => {
        if (event.type === "LEAD_CREATED" && event.data) {
          return event.data.projectId;
        }
        return undefined;
      },
    }),
    saveStepResult: assign({
      isDisqualified: ({ event }: any) => event.output?.isDisqualified,
      needsReconfirmation: ({ event }: any) =>
        event.output?.needsReconfirmation,
      projectId: ({ event, context }: any) =>
        event.output?.projectId ?? context.projectId,
    }),
    savePropertyStepResult: assign({
      isDisqualified: ({ event }: any) => event.output?.isDisqualified,
      needsReconfirmation: ({ event }: any) =>
        event.output?.needsReconfirmation,
      projectId: ({ event, context }: any) =>
        event.output?.projectId ?? context.projectId,
      requirements: ({ event, context }: any) => {
        const resolvedNewProject =
          !context.projectId && !!event.output?.projectId;
        if (!resolvedNewProject || !event.output?.requirements) {
          return context.requirements;
        }
        return {
          ...event.output.requirements,
          postalCode: true,
          purchasePrice: true,
          hasPastMeetings: context.requirements?.hasPastMeetings,
          project: context.requirements?.project,
        };
      },
    }),
    confirmExistingProject: assign({
      projectDecisionMade: () => true,
    }),
    startNewProject: assign({
      projectId: () => undefined,
      dealId: () => undefined,
      matchedProject: () => undefined,
      projectDecisionMade: () => true,
      firstStepId: () => "property",
      requirements: ({ context }) =>
        context.requirements
          ? { ...context.requirements, postalCode: true, purchasePrice: true }
          : context.requirements,
    }),
    saveReconfirmationResult: assign({
      phoneNumber: ({ event }) => {
        if (event.type === "RECONFIRMATION") {
          return event.data.phoneNumber;
        }
      },
    }),
    trackCalendarShown: ({ context }) => {
      if (!context.leadId) return;
      fetchProjectUpdateBookingCalendarShown({
        pathParams: { leadId: context.leadId },
      }).catch((error) => {
        logger.error(
          `Could not track calendar shown with leadId: ${context.leadId}`,
          error,
        );
      });
    },
    trackReconfirmationFormShown: async ({ context }) => {
      if (!context.leadId || !context.projectId) return;
      try {
        await fetchProjectUpdateBookingReconfirmationFormShown({
          pathParams: { leadId: context.leadId, projectId: context.projectId },
        });
      } catch (error) {
        logger.error(
          `Could not track booking reconfirmation form shown with leadId: ${context.leadId} and projectId: ${context.projectId}`,
          error,
        );
      }
    },
  },
}).createMachine({
  id: "qualification",
  initial: "start",
  context: ({ input }: any) => ({
    formData: {},
    ...input,
  }),
  states: {
    start: {
      id: "start",
      always: [
        {
          target: "leadWall",
          guard: "needsLeadWall",
        },
        {
          target: "calendar",
          guard: "isSkipQualification",
        },
        {
          target: "getRequirements",
        },
      ],
    },
    leadWall: {
      id: "leadWall",
      on: {
        LEAD_CREATED: {
          target: "getRequirements",
          actions: ["setLead"],
        },
      },
    },
    getRequirements: {
      id: "getRequirements",
      invoke: {
        src: "getRequirements",
        input: ({ context }) => ({
          leadId: context.leadId,
          projectId: context.projectId,
          partnerId: context.partnerId,
          dealId: context.dealId,
        }),
        onDone: {
          actions: assign({
            requirements: ({ event }) => event.output?.requirements,
            matchedProject: ({ event }) => event.output?.requirements?.project,
            projectId: ({ event, context }) =>
              event.output?.qualification.projectId ?? context.projectId,
            isDisqualified: ({ event }) =>
              event.output?.qualification.disqualified,
            needsReconfirmation: ({ event }) =>
              event.output?.qualification.needsReconfirmation,
          }),
          target: "determineFirstQualificationStep",
        },
      },
    },
    determineFirstQualificationStep: {
      id: "determineFirstQualificationStep",
      always: [
        {
          target: "projectConfirmation",
          guard: "hasUnconfirmedMatchedProject",
        },
        {
          target: "calendar",
          guard: "hasPastMeetings",
        },
        {
          target: "disqualified",
          guard: "isDisqualified",
        },
        {
          target: "property",
          guard: "needsProperty",
          actions: [{ type: "setFirstStepId", params: "property" }],
        },
        {
          target: "income",
          guard: "needsIncome",
          actions: [{ type: "setFirstStepId", params: "income" }],
        },
        {
          target: "equity",
          guard: "needsEquity",
          actions: [{ type: "setFirstStepId", params: "equity" }],
        },
        {
          target: "residence",
          guard: "needsResidence",
          actions: [{ type: "setFirstStepId", params: "residence" }],
        },
        {
          target: "employment",
          guard: "needsEmployment",
          actions: [{ type: "setFirstStepId", params: "employment" }],
        },
        {
          target: "reconfirmation",
          guard: "needsReconfirmation",
        },
        {
          target: "propertyViewingStatus",
          guard: "needsPropertyViewingStatus",
          actions: [
            { type: "setFirstStepId", params: "propertyViewingStatus" },
          ],
        },
        {
          target: "decideOnReconfirmation",
        },
      ],
    },
    projectConfirmation: {
      id: "projectConfirmation",
      on: {
        PROJECT_DECISION: [
          {
            target: "property",
            guard: ({ event }) => event.data === "new",
            actions: ["startNewProject"],
          },
          {
            target: "reactivatingProject",
            actions: ["confirmExistingProject"],
          },
        ],
      },
    },
    reactivatingProject: {
      id: "reactivatingProject",
      tags: ["loading"],
      invoke: {
        src: "reactivateProject",
        input: ({ context }) => ({
          leadId: context.leadId!,
          projectId: context.projectId!,
        }),
        onDone: { target: "determineFirstQualificationStep" },
        onError: {
          target: "determineFirstQualificationStep",
          actions: ({ event }) =>
            logger.error("Could not reactivate project.", event.error),
        },
      },
    },
    property: {
      id: "property",
      on: {
        NEXT: "propertySaving",
      },
    },
    propertySaving: {
      id: "propertySaving",
      entry: assign({ isError: false }),
      tags: ["loading"],
      invoke: {
        src: "saveStep",
        input: makeSaveStepInput(),
        onDone: [
          {
            guard: "isDisqualifiedFromOutput",
            actions: ["saveStepResult"],
            target: "disqualified",
          },
          {
            actions: ["savePropertyStepResult"],
            target: "determineStepAfterProperty",
          },
        ],
        onError: {
          target: "property",
          actions: assign({ isError: true }),
        },
      },
    },
    determineStepAfterProperty: {
      id: "determineStepAfterProperty",
      always: [
        {
          target: "income",
          guard: "needsIncome",
        },
        {
          target: "equity",
          guard: "needsEquity",
        },
        {
          target: "residence",
          guard: "needsResidence",
        },
        {
          target: "employment",
          guard: "needsEmployment",
        },
        {
          target: "propertyViewingStatus",
          guard: "needsPropertyViewingStatus",
        },
        {
          target: "decideOnReconfirmation",
        },
      ],
    },
    income: {
      id: "income",
      on: {
        NEXT: "incomeSaving",
        BACK: [
          {
            target: "property",
            guard: "needsProperty",
          },
        ],
      },
    },
    incomeSaving: {
      id: "incomeSaving",
      entry: assign({ isError: false }),
      tags: ["loading"],
      invoke: {
        src: "saveStep",
        input: makeSaveStepInput("income"),
        onDone: [
          {
            guard: "isDisqualifiedFromOutput",
            actions: ["saveStepResult"],
            target: "disqualified",
          },
          {
            actions: ["saveStepResult"],
            target: "equity",
            guard: "needsEquity",
          },
          {
            actions: ["saveStepResult"],
            target: "residence",
            guard: "needsResidence",
          },
          {
            actions: ["saveStepResult"],
            target: "employment",
            guard: "needsEmployment",
          },
          {
            actions: ["saveStepResult"],
            target: "propertyViewingStatus",
            guard: "needsPropertyViewingStatus",
          },
          {
            actions: ["saveStepResult"],
            target: "decideOnReconfirmation",
          },
        ],
        onError: {
          target: "income",
          actions: assign({ isError: true }),
        },
      },
    },
    equity: {
      id: "equity",
      on: {
        NEXT: "equitySaving",
        BACK: [
          {
            target: "income",
            guard: "needsIncome",
          },
          {
            target: "property",
            guard: "needsProperty",
          },
        ],
      },
    },
    equitySaving: {
      id: "equitySaving",
      entry: assign({ isError: false }),
      tags: ["loading"],
      invoke: {
        src: "saveStep",
        input: makeSaveStepInput("equity"),
        onDone: [
          {
            guard: "isDisqualifiedFromOutput",
            actions: ["saveStepResult"],
            target: "disqualified",
          },
          {
            actions: ["saveStepResult"],
            target: "residence",
            guard: "needsResidence",
          },
          {
            actions: ["saveStepResult"],
            target: "employment",
            guard: "needsEmployment",
          },
          {
            actions: ["saveStepResult"],
            target: "propertyViewingStatus",
            guard: "needsPropertyViewingStatus",
          },
          {
            actions: ["saveStepResult"],
            target: "decideOnReconfirmation",
          },
        ],
        onError: {
          target: "equity",
          actions: assign({ isError: true }),
        },
      },
    },
    residence: {
      id: "residence",
      on: {
        NEXT: "residenceSaving",
        BACK: [
          {
            target: "equity",
            guard: "needsEquity",
          },
          {
            target: "income",
            guard: "needsIncome",
          },
          {
            target: "property",
            guard: "needsProperty",
          },
        ],
      },
    },
    residenceSaving: {
      id: "residenceSaving",
      entry: assign({ isError: false }),
      tags: ["loading"],
      invoke: {
        src: "saveStep",
        input: makeSaveStepInput("residence"),
        onDone: [
          {
            guard: "isDisqualifiedFromOutput",
            actions: ["saveStepResult"],
            target: "disqualified",
          },
          {
            actions: ["saveStepResult"],
            target: "employment",
            guard: "needsEmployment",
          },
          {
            actions: ["saveStepResult"],
            target: "propertyViewingStatus",
            guard: "needsPropertyViewingStatus",
          },
          {
            actions: ["saveStepResult"],
            target: "decideOnReconfirmation",
          },
        ],
        onError: {
          target: "residence",
          actions: assign({ isError: true }),
        },
      },
    },
    employment: {
      id: "employment",
      on: {
        NEXT: "employmentSaving",
        BACK: [
          {
            target: "residence",
            guard: "needsResidence",
          },
          {
            target: "equity",
            guard: "needsEquity",
          },
          {
            target: "income",
            guard: "needsIncome",
          },
          {
            target: "property",
            guard: "needsProperty",
          },
        ],
      },
    },
    employmentSaving: {
      id: "employmentSaving",
      entry: assign({ isError: false }),
      tags: ["loading"],
      invoke: {
        src: "saveStep",
        input: makeSaveStepInput("employment"),
        onDone: [
          {
            guard: "isDisqualifiedFromOutput",
            actions: ["saveStepResult"],
            target: "disqualified",
          },
          {
            actions: ["saveStepResult"],
            target: "propertyViewingStatus",
            guard: "needsPropertyViewingStatus",
          },
          {
            actions: ["saveStepResult"],
            target: "decideOnReconfirmation",
          },
        ],
        onError: {
          target: "employment",
          actions: assign({ isError: true }),
        },
      },
    },
    propertyViewingStatus: {
      id: "propertyViewingStatus",
      on: {
        NEXT: "propertyViewingStatusSaving",
        BACK: [
          {
            target: "employment",
            guard: "needsEmployment",
          },
          {
            target: "residence",
            guard: "needsResidence",
          },
          {
            target: "equity",
            guard: "needsEquity",
          },
          {
            target: "income",
            guard: "needsIncome",
          },
          {
            target: "property",
            guard: "needsProperty",
          },
        ],
      },
    },
    propertyViewingStatusSaving: {
      id: "propertyViewingStatusSaving",
      entry: assign({ isError: false }),
      tags: ["loading"],
      invoke: {
        src: "saveStep",
        input: makeSaveStepInput("propertyViewingStatus"),
        onDone: [
          {
            guard: "isDisqualifiedFromOutput",
            actions: ["saveStepResult"],
            target: "disqualified",
          },
          {
            actions: ["saveStepResult"],
            target: "decideOnReconfirmation",
          },
        ],
        onError: {
          target: "propertyViewingStatus",
          actions: assign({ isError: true }),
        },
      },
    },
    decideOnReconfirmation: {
      id: "decideOnReconfirmation",
      always: [
        {
          target: "reconfirmation",
          guard: "needsReconfirmation",
        },
        {
          target: "calendar",
        },
      ],
    },
    reconfirmation: {
      id: "reconfirmation",
      entry: ["trackReconfirmationFormShown"],
      on: {
        RECONFIRMATION: [
          {
            target: "reconfirmationSaving",
            actions: ["saveReconfirmationResult"],
          },
        ],
        BACK: [
          {
            target: "propertyViewingStatus",
            guard: "needsPropertyViewingStatus",
          },
          {
            target: "employment",
            guard: "needsEmployment",
          },
          {
            target: "residence",
            guard: "needsResidence",
          },
          {
            target: "equity",
            guard: "needsEquity",
          },
          {
            target: "income",
            guard: "needsIncome",
          },
          {
            target: "property",
            guard: "needsProperty",
          },
        ],
      },
    },
    reconfirmationSaving: {
      id: "reconfirmationSaving",
      entry: assign({ isError: false }),
      tags: ["loading"],
      invoke: {
        src: "saveReconfirmation",
        input: ({ context }) => ({
          leadId: context.leadId,
          projectId: context.projectId,
          partnerId: context.partnerId,
          phoneNumber: context.phoneNumber!,
        }),
        onDone: "reconfirmationComplete",
        onError: {
          target: "reconfirmation",
          actions: assign({ isError: true }),
        },
      },
    },
    reconfirmationComplete: {
      id: "reconfirmationComplete",
      type: "final",
      entry: [],
    },
    calendar: {
      id: "calendar",
      on: {
        MEETING_SCHEDULED: {
          target: "calendarSaving",
          actions: ["setMeetingTime"],
        },
      },
      entry: ["trackCalendarShown"],
    },
    calendarSaving: {
      id: "calendarSaving",
      entry: assign({ isError: false }),
      tags: ["loading"],
      invoke: {
        src: "saveLeadCallAction",
        input: ({ context }) => ({
          leadId: context.leadId!,
          partnerId: context.partnerId,
          propertyId: context.propertyId,
          projectId: context.projectId,
          meetingTime: context.meetingTime,
        }),
        onDone: {
          target: "calendarComplete",
        },
        onError: {
          target: "calendar",
          actions: assign({ isError: true }),
        },
      },
    },
    calendarComplete: {
      id: "calendarComplete",
      type: "final",
      entry: [],
    },
    disqualified: {
      id: "disqualified",
      type: "final",
      entry: [],
    },
  },
});

export type QualificationMachine = typeof qualificationMachine;
export type QualificationState = ReturnType<QualificationMachine["transition"]>;
export type QualificationStateId = QualificationState["value"];
